import { CountryDetectorFactors } from "../enums/CountryDetectorFactors";
import { CountriesManager } from "../manager/CountriesManager";
import { CountryDetectionVO } from "../model/CountryDetectionVO";
import { Country } from "../model/Country";
import { getLogger } from "@goodgamestudios/logger";
import { Context } from "../../../core/impl/Context";
import { Service } from "../../../core/impl/Service";
import { GeoIPUtils } from "../helpers/GeoIPUtils";
import { filterCountriesByBrowserLanguage, filterCountriesByTimezone } from "../helpers/CountryFilters";
import { CountryDetectionConstants } from "../enums/CountryDetectionConstants";

const logger = getLogger("WorldAssignment.CountryDetector");
/**
 * Tries to detect the correct country for the player by looking at various data or even requesting a country from our geoIP server if everything else fails.
 *
 * Order of detection priority (as soon as one of those methods returns a country, the detection looks no further)
 *
 * 1. country dictated by preset instance
 * 2. country dictated by friend invite code
 * 3. country dictated by default instance (from network settings)
 * 4. country dictated by local storage
 * 5. country dictated by landing page
 * 6. country derived from browser language
 */
export const runCountryDetection = async (_vo: CountryDetectionVO, _countriesManager: CountriesManager): Promise<{ country: Country, factor: CountryDetectorFactors }> => {
    let _country: Country;
    let _detectionFactor: CountryDetectorFactors;


    logger.debug("CountryDetector.run { " + _vo.toString() + " }");

    try {
        if (_vo.countryByPresetInstance) {
            _detectionFactor = CountryDetectorFactors.FACTOR_BY_INSTANCE;
            _country = _countriesManager.getCountryByCountryCode(_vo.countryByPresetInstance, _vo.alsoDetectUnavailableCountries);

        }

        if (!_country && _vo.zoneIdFromFriendInviteCode) {
            _detectionFactor = CountryDetectorFactors.FACTOR_BY_FRIEND_INVITE_CODE;
            _country = _countriesManager.getCountryByCountryCode(_vo.zoneIdFromFriendInviteCode, _vo.alsoDetectUnavailableCountries);
        }

        if (!_country && _vo.countryByDefaultInstance && _vo.countryByDefaultInstance !== CountryDetectionConstants.INSTANCE_COUNTRY_DEFAULT) {
            _detectionFactor = CountryDetectorFactors.FACTOR_BY_DEFAULT_INSTANCE;
            _country = _countriesManager.getCountryByCountryCode(_vo.countryByDefaultInstance, _vo.alsoDetectUnavailableCountries);
        }

        if (!_country && _vo.countryCodeFromNetworkCookie) {
            _detectionFactor = CountryDetectorFactors.FACTOR_BY_NETWORK_COOKIE;
            _country = _countriesManager.getCountryByCountryCode(_vo.countryCodeFromNetworkCookie, _vo.alsoDetectUnavailableCountries);
        }

        if (!_country && _vo.countryCodeFromQueryString) {
            _detectionFactor = CountryDetectorFactors.FACTOR_BY_QUERY_STRING;
            _country = _countriesManager.getCountryByCountryCode(_vo.countryCodeFromQueryString, _vo.alsoDetectUnavailableCountries);
        }

        if (!_country && _vo.browserLanguage && _vo.browserLanguage !== "n.a.") {
            let countriesToFilter = _vo.alsoDetectUnavailableCountries ? _countriesManager.allCountries : _countriesManager.activeCountries;
            let countries: Country[] = filterCountriesByBrowserLanguage(countriesToFilter, _vo.browserLanguage);
            if (countries.length === 1) {
                _detectionFactor = CountryDetectorFactors.FACTOR_BY_BROWSER_LANGUAGE;
                _country = countries[0];
            }
            if (countries.length > 1) {
                countries = filterCountriesByTimezone(countries, _vo.timezoneOffset);
                if (countries.length === 1) {
                    _detectionFactor = CountryDetectorFactors.FACTOR_BY_BROWSER_LANGUAGE_AND_TIMEZONE;
                    _country = countries[0];
                }
            }
        }
        if (!_country) {
            const geoIpService = new Service(Context.instance.globals.geoIPUrl);
            _detectionFactor = CountryDetectorFactors.FACTOR_BY_GEOIP;
            _country = await geoIpService.load()
                .then(payload => onGeoIPReceived(payload))
                .then(code => _countriesManager.getCountryByGeoIpCode(code, _vo.alsoDetectUnavailableCountries));
        }
    }
    catch (error) {
        logger.error("Error while detecting country", error);
    }
    if (!_country) {
        _country = _countriesManager.defaultCountry;
        _detectionFactor = CountryDetectorFactors.FACTOR_BY_FALLBACK;
    }

    logger.debug(`Country was detected by ${CountryDetectorFactors[_detectionFactor]}:  ${_country}`);
    return { country: _country, factor: _detectionFactor };

};


/**
 * Decodes countrycode received by geoip service
 * @param encodedData
 * @returns {Country}
 */
const onGeoIPReceived = async (encodedData: any): Promise<string> => {
    logger.debug("onGeoIPReceived");
    let bytes: number[] = GeoIPUtils.hexStringToBytes(encodedData);
    let ascii: string = GeoIPUtils.bytesToAsciiString(bytes);
    let countryCode: string = window.atob(ascii);
    logger.debug("onGeoIPReceived  countryCode", countryCode);
    return countryCode;
}

